import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../AuthProvider";
import {
  fetchListmonkStatus,
  getListmonkAdminUrl,
  synchronizeListmonk,
} from "../services/listmonkService";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("de-CH", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

export default function ListmonkPage() {
  const { fetchWithAuth } = useAuth();
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchListmonkStatus(fetchWithAuth);
      setLastRun(payload.last_run || null);
    } catch (err) {
      setError(err.message || "Listmonk-Status konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = await synchronizeListmonk(fetchWithAuth);
      setSuccess(payload.metrics || null);
      await loadStatus();
    } catch (err) {
      setError(err.message || "Listmonk-Synchronisierung fehlgeschlagen.");
    } finally {
      setSyncing(false);
    }
  };

  const metrics = lastRun?.metrics || {};
  const result = success || metrics;

  return (
    <section className="portal-page">
      <div className="portal-page-header">
        <div>
          <h1>Kommunikation</h1>
          <p>Verwalte Kampagnen, Policy-Updates und Empfänger in Listmonk.</p>
        </div>
        <a
          className="portal-button"
          href={getListmonkAdminUrl()}
          target="_blank"
          rel="noreferrer"
        >
          Listmonk öffnen
        </a>
      </div>

      {error && <div className="portal-error">{error}</div>}
      {success && <div className="portal-success">Synchronisierung abgeschlossen.</div>}

      <div className="portal-card">
        <div className="portal-card-header">
          <div>
            <h2>Keycloak-Synchronisierung</h2>
            <p>Marketing-Abos bleiben ausschließlich in Listmonk.</p>
          </div>
          <button className="portal-button" onClick={handleSync} disabled={syncing}>
            {syncing ? "Synchronisiere…" : "Jetzt synchronisieren"}
          </button>
        </div>
        {loading ? (
          <p>Status wird geladen…</p>
        ) : (
          <>
            <p>Letzter Start: {formatDate(lastRun?.started_at)}</p>
            <p>Letztes Ende: {formatDate(lastRun?.finished_at)}</p>
            {lastRun ? (
              <div className="portal-stat-grid">
                {[
                  ["Erstellt", result.created],
                  ["Aktualisiert", result.updated],
                  ["Deaktiviert", result.disabled],
                  ["Übersprungen", result.skipped],
                  ["Entfernt", result.removed],
                  ["Fehler", result.failed],
                ].map(([label, value]) => (
                  <div className="portal-stat" key={label}>
                    <strong>{value ?? 0}</strong>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p>Noch keine Synchronisierung ausgeführt.</p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
